import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

type BudgetStatus = 'normal' | 'at-risk' | 'over-budget'

interface BudgetSummaryCardProps {
  label: string
  amountCents: number
  currency: string
  status?: BudgetStatus
}

const getAmountColor = (status?: BudgetStatus) => {
  switch (status) {
    case 'over-budget':
      return 'radicalRed1000'
    case 'at-risk':
      return 'moodNegative'
    default:
      return 'grey1000'
  }
}

const BudgetSummaryCard = ({ label, amountCents, currency, status }: BudgetSummaryCardProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  return (
    <Box
      borderRadius={{ all: 'abs012' }}
      border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
      flexDirection="column"
      padding="s16"
      gap="s8"
      width="full"
      flexGrow
    >
      <Text.Normal size="200" color="grey800">
        {label}
      </Text.Normal>
      <Text.Header2 color={getAmountColor(status)}>
        {amountWithCurrency(amountCents, currency, locale)}
      </Text.Header2>
    </Box>
  )
}

interface BudgetSummaryCardsProps {
  totalBudgetCents: number
  allocationCents: number
  availableCents: number
  currency: string
}

const getBudgetStatus = (totalBudgetCents: number, availableCents: number): BudgetStatus => {
  if (availableCents < 0) {
    return 'over-budget'
  }

  // At risk when 10% or less is available
  const atRiskThreshold = totalBudgetCents * 0.1
  if (availableCents <= atRiskThreshold) {
    return 'at-risk'
  }

  return 'normal'
}

export const BudgetSummaryCards = ({
  totalBudgetCents,
  allocationCents,
  availableCents,
  currency,
}: BudgetSummaryCardsProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const budgetStatus = getBudgetStatus(totalBudgetCents, availableCents)
  const exceededAmount = amountWithCurrency(Math.abs(availableCents), currency, locale)

  return (
    <Box flexDirection="column" gap="s16" width="full">
      <Box flexDirection="row" gap="s16" width="full">
        <BudgetSummaryCard
          label={i18n.t('budgets.summary.total_budget')}
          amountCents={totalBudgetCents}
          currency={currency}
        />
        <BudgetSummaryCard
          label={i18n.t('budgets.summary.allocation')}
          amountCents={allocationCents}
          currency={currency}
        />
        <BudgetSummaryCard
          label={i18n.t('budgets.summary.available')}
          amountCents={availableCents}
          currency={currency}
          status={budgetStatus}
        />
      </Box>

      {budgetStatus === 'at-risk' && (
        <F0Alert
          variant="warning"
          title={i18n.t('budgets.summary.at_risk_title')}
          description={i18n.t('budgets.summary.at_risk_description')}
        />
      )}

      {budgetStatus === 'over-budget' && (
        <F0Alert
          variant="critical"
          title={i18n.t('budgets.summary.over_budget_title')}
          description={i18n.t('budgets.summary.over_budget_description', {
            amount: exceededAmount,
          })}
        />
      )}
    </Box>
  )
}
